import { SHAPE_SIZES } from './model';
import { LABEL_FONT } from './network/constants';

export const DISPLAY_ID = 'id';
export const DISPLAY_LABEL = 'label';

export const SHAPE_FAMILY = {
  dot: 'round',
  ellipse: 'round',
  square: 'rect',
  box: 'rect',
  image: 'image',
};

export const SHAPES_FOR_MODE = {
  [DISPLAY_ID]: [
    { value: 'dot', label: 'Circle' },
    { value: 'square', label: 'Square' },
  ],
  [DISPLAY_LABEL]: [
    { value: 'ellipse', label: 'Ellipse' },
    { value: 'box', label: 'Rectangle' },
    { value: 'image', label: 'Icon' },
  ],
};

export const isIdMode = (mode) => mode === DISPLAY_ID;

/** Rectangles fill their box; ellipses sit inside it. Draw boxes at 90% so the same width/height look similar. */
export const BOX_DISPLAY_SCALE = 0.9;

/** Stored width/height from a resize that was dragged on the drawn rectangle. */
export const storedSizeFromDisplay = (shape, display) => {
  if (shape !== 'box') return display;
  return {
    ...display,
    width: display.width / BOX_DISPLAY_SCALE,
    height: display.height / BOX_DISPLAY_SCALE,
  };
};

export const shapeFamily = (shape) => SHAPE_FAMILY[shape] || 'round';

/** The shape drawn for a stored node in this display mode. */
export const presentShape = (shape, mode) => {
  const family = shapeFamily(shape);
  if (isIdMode(mode)) return family === 'rect' ? 'square' : 'dot';
  if (family === 'image') return 'image';
  return family === 'rect' ? 'box' : 'ellipse';
};

/** Keep the current default if it belongs in `mode`, otherwise the mode's usual default. */
export const defaultShapeForMode = (mode, current) => {
  const allowed = SHAPES_FOR_MODE[isIdMode(mode) ? DISPLAY_ID : DISPLAY_LABEL]
    .map((item) => item.value);
  if (allowed.includes(current)) return current;
  const family = shapeFamily(current);
  if (isIdMode(mode)) return family === 'rect' ? 'square' : 'dot';
  if (family === 'rect') return 'box';
  if (family === 'image') return 'image';
  return 'ellipse';
};

/**
 * View of a node for the current display mode: compact ID chips, or labelled
 * ellipses / rectangles / icons. Does not rewrite the stored model.
 */
/** Stored width/height only after a resize; otherwise the shape's default. */
export const sizeForShape = (node, shape) => {
  if (node?.resized === true && node.width > 0 && node.height > 0) {
    return { width: node.width, height: node.height };
  }
  const size = SHAPE_SIZES[shape] || SHAPE_SIZES.ellipse;
  return { width: size.width, height: size.height };
};

export const presentNode = (node, mode) => {
  const shape = presentShape(node.shape, mode);
  const label = String(node.label ?? '').trim();
  // ID chips show the label when there is one (short symbols like $x_1$), else the ID
  const caption = isIdMode(mode) ? label || String(node.id) : String(node.label ?? node.id ?? '');
  if (isIdMode(mode)) {
    const size = SHAPE_SIZES[shape];
    return {
      ...node, shape, caption, width: size.width, height: size.height,
    };
  }
  if (shape === 'image') return { ...node, shape, caption };
  const size = sizeForShape(node, shape);
  return {
    ...node,
    shape,
    caption,
    width: size.width,
    height: size.height,
  };
};

const defaultMeasure = (text, fontSize) => [...String(text)].length * fontSize * 0.55;

/** Split `text` into lines that fit `maxWidth`. `measure(text)` returns px width. */
export const wrapText = (text, maxWidth, measure) => {
  const widthOf = measure || ((line) => defaultMeasure(line, LABEL_FONT.size));
  const fit = Math.max(8, maxWidth);
  const lines = [];
  String(text ?? '').split('\n').forEach((paragraph) => {
    if (paragraph === '') {
      lines.push('');
      return;
    }
    let current = '';
    paragraph.split(/\s+/).forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (widthOf(next) <= fit) {
        current = next;
        return;
      }
      if (current) lines.push(current);
      if (widthOf(word) <= fit) {
        current = word;
        return;
      }
      let chunk = '';
      [...word].forEach((ch) => {
        if (widthOf(chunk + ch) <= fit) {
          chunk += ch;
        } else {
          if (chunk) lines.push(chunk);
          chunk = ch;
        }
      });
      current = chunk;
    });
    if (current) lines.push(current);
  });
  return lines.length > 0 ? lines : [''];
};

const INNER = { box: 6, ellipse: 0.9 };

export const labelBox = (shape, size) => {
  if (shape === 'ellipse') {
    return { width: size.width * INNER.ellipse, height: size.height * INNER.ellipse };
  }
  return { width: Math.max(8, size.width - INNER.box), height: Math.max(8, size.height - INNER.box) };
};

/** IDs longer than this shrink on the chip instead of overflowing. */
export const ID_FIT_CHARS = 3;

/** Font size for an ID chip. Three characters stay at full size; longer IDs scale down. */
export const fitId = (text, baseSize = LABEL_FONT.size, minSize = 8) => {
  const caption = String(text ?? '');
  const chars = [...caption].length;
  if (chars <= ID_FIT_CHARS) return { fontSize: baseSize, caption };
  return {
    fontSize: Math.max(minSize, (baseSize * ID_FIT_CHARS) / chars),
    caption,
  };
};

/** Font size that lets wrapped `text` sit inside the shape. */
export const fitLabel = (text, shape, size, baseSize = LABEL_FONT.size, minSize = 10) => {
  const box = labelBox(shape, size);
  const wrapAt = (fontSize) => wrapText(text, box.width, (line) => defaultMeasure(line, fontSize));
  let fontSize = baseSize;
  let lines = wrapAt(fontSize);
  while (fontSize > minSize) {
    const lineHeight = fontSize * 1.2;
    if (lines.length * lineHeight <= box.height) break;
    fontSize -= 1;
    lines = wrapAt(fontSize);
  }
  return { fontSize, lines, lineHeight: fontSize * 1.2 };
};

const MIN_NODE = 48;

/** New centre and size after dragging resize handle `dir` by world delta. */
export const resizeNode = (node, size, dir, dx, dy) => {
  let { x, y } = node;
  let width = size.width;
  let height = size.height;
  if (dir.includes('e')) {
    width = Math.max(MIN_NODE, width + dx);
    x += (width - size.width) / 2;
  }
  if (dir.includes('w')) {
    width = Math.max(MIN_NODE, width - dx);
    x -= (width - size.width) / 2;
  }
  if (dir.includes('s')) {
    height = Math.max(MIN_NODE, height + dy);
    y += (height - size.height) / 2;
  }
  if (dir.includes('n')) {
    height = Math.max(MIN_NODE, height - dy);
    y -= (height - size.height) / 2;
  }
  return { x, y, width, height };
};
